import React, {useCallback} from 'react';
import {observer} from 'mobx-react';
import {
    Button,
    Draggable,
    Drawer,
    Flex,
    Icon,
    PriorityIcon,
    Text,
    CheckBox
} from '@priority-software/priority-style-react';
import {priority} from '@priority-software/priority-proto-api/lib';
import cx from 'classnames';
import {isRTL} from '../../../../../Localization';
import {texts} from './texts';
import {DraggableList} from '../../../../../components/commonOldtoNew';
import {SavedSearch} from '../../../types';
import {Form, apiLocker} from '../../../../../stores/Form';
import {FilterItem} from './components/FilterItem/FilterItem';
import {ColumnsDropdown} from './components/ColumnsDropdown/ColumnsDropdown';
import {ISearchCondition} from '../../../../AppSearches/types/filters';
import {ISort} from '../../../../../stores/Form/Sort';
import {createDataTestId} from '../../../../../utils/dataTestId';
import {AdvancedSearchFooter} from './components/Footer/Footer';
import {useAdvancedSearch} from './hooks/useAdvancedSearch';
import {AdvancedSearchHeader} from './components/Header/AdvancedSearchHeader';
import {AIFilterSearchContext} from './components/AISearchBlock/AIFilterSearch.context';
import {useAIFilterSearch} from './components/AISearchBlock/hooks/useAIFilterSearch';
import {AISection} from './components/AISearchBlock/AISection';
import {isSearchDisabled} from './utils';
import styles from './styles.module.scss';

type Props = {
    mainSearchButton?: boolean;
    isOpen: boolean;
    form: Form;
    editSearch?: PatchSavedSearch;
    onClose: () => void;
    onReorder: (data: SavedSearch[]) => void;
    handleDeleteSearch: (id: string) => void;
    handleToggleDefault: (id: string) => void;
    zIndex?: number;
};

export type PatchFilter = {
    Condition: ISearchCondition[];
    Operator: priority.netitems.shared.LogicalOperator;
    sort?: ISort;
};

export interface PatchSavedSearch extends Omit<SavedSearch, 'filters'> {
    //The interface uses a different filters structure, but the rest fields are the same
    filters: PatchFilter[];
}

export const AdvancedSearchDrawer = observer(
    ({
        isOpen,
        form,
        editSearch,
        onClose,
        onReorder,
        handleDeleteSearch,
        handleToggleDefault,
        zIndex
    }: Props) => {
        const {title: formName, columns} = form;
        const {recentSearches, savedSearches} = form.searchStore;

        const {state, handlers} = useAdvancedSearch({form, onClose, editSearch});
        const {showFilterFieldDropdown, search, ignoreCase, isAISearchAllowed} = state;
        const isSavedSearch = savedSearches.findIndex(s => s.id === search?.id) > -1;

        const {
            setShowFilterInput,
            onSearch,
            saveAndSearch,
            handleReorder,
            handleExecuteSearch,
            handleUpdateFilter,
            handleRemoveFilter,
            handleIgnoreCase,
            clearSearch,
            closeSearch,
            onAddFilter,
            setFilters,
            updateTitle
        } = handlers;

        const {state: AIState, handlers: AIHandlers} = useAIFilterSearch({
            formTitle: formName,
            columns,
            clearFilters: clearSearch,
            setSearchFilters: setFilters
        });

        const {handleAiSearch, showAISearch, resetError} = AIHandlers;

        const onSearchClear = useCallback(() => {
            resetError();
            setShowFilterInput(true);
            clearSearch();
        }, [clearSearch, resetError, setShowFilterInput]);

        const currScreenLockerData = apiLocker.getDataBySession(form.session);
        const isDisabled = isSearchDisabled(search) || currScreenLockerData?.locked;
        const filterCount = search?.filters?.length;
        const displayAIsection = isAISearchAllowed && !search?.isSaved;

        return (
            <AIFilterSearchContext.Provider value={AIState}>
                <Drawer
                    zIndex={zIndex}
                    width={620}
                    open={isOpen}
                    onClose={closeSearch}
                    placement="right"
                    rtl={isRTL()}
                >
                    <div
                        className={cx('reactComp', styles.drawer)}
                        {...createDataTestId('advanced-search-container', false)}
                    >
                        <AdvancedSearchHeader
                            session={form.session}
                            formName={formName}
                            searchTitle={search?.name}
                            filterCount={filterCount}
                            onDrawerClose={closeSearch}
                            handleExecuteSearch={handleExecuteSearch}
                            handleDeleteSearch={handleDeleteSearch}
                            updateTitle={updateTitle}
                            handleToggleDefault={handleToggleDefault}
                            onReorder={onReorder}
                            searches={savedSearches}
                            recentSearches={recentSearches}
                            isSavedSearch={isSavedSearch}
                        />

                        <div className={styles.container}>
                            <Flex
                                justifyContent="space-between"
                                componentClasses={styles.ignoreCaseContainer}
                                {...createDataTestId('ignore-case-container', true)}
                            >
                                <CheckBox
                                    componentClasses={styles.checkbox}
                                    checked={ignoreCase}
                                    onChange={handleIgnoreCase}
                                    disabled={!search?.filters?.length}
                                >
                                    <Text
                                        skin="paragraph-2"
                                        textColor="primary-700"
                                        componentClasses={styles.whiteSpace}
                                    >
                                        {texts.ignoreCase}
                                    </Text>
                                </CheckBox>
                                <Text skin="label-1" textColor={'secondary-500'}>
                                    {texts.drawerHelpText}
                                </Text>
                            </Flex>

                            {displayAIsection ? (
                                <AISection
                                    handleAiSearch={handleAiSearch}
                                    showAISearch={showAISearch}
                                    filterCount={filterCount}
                                />
                            ) : null}

                            {!AIState.isLoading ? (
                                <>
                                    <DraggableList
                                        handleDNDFinish={handleReorder}
                                        componentClasses={styles.draggablelist}
                                        gradientScroll
                                        data={search?.filters || []}
                                        renderItem={({item, index, ...draggableProps}) => {
                                            return (
                                                <Draggable
                                                    componentClasses={styles.listItem}
                                                    {...draggableProps}
                                                    iconPosition="center"
                                                    key={item.Condition[0]?.columnID as number}
                                                >
                                                    <FilterItem
                                                        form={form}
                                                        onRemoveFilter={handleRemoveFilter}
                                                        key={item.Condition[0]?.columnID as number}
                                                        filter={item}
                                                        updateFilter={handleUpdateFilter}
                                                    />
                                                </Draggable>
                                            );
                                        }}
                                    />

                                    {showFilterFieldDropdown ? (
                                        <ColumnsDropdown
                                            columnsData={columns}
                                            onSelect={onAddFilter}
                                            autoFocus={!displayAIsection}
                                        />
                                    ) : (
                                        <Button
                                            componentClasses={styles.plusSearch}
                                            onClick={() => setShowFilterInput(true)}
                                        >
                                            <Icon
                                                icon={PriorityIcon.add}
                                                componentClasses={styles.iconPlus}
                                            />
                                        </Button>
                                    )}
                                </>
                            ) : null}
                        </div>

                        <AdvancedSearchFooter
                            onClear={onSearchClear}
                            showExportBtn={false} // should be true when the feature is implemented
                            isDisabled={isDisabled}
                            search={search}
                            onSearch={onSearch}
                            onSaveAndSearch={saveAndSearch}
                            isSavedSearch={isSavedSearch}
                        />
                    </div>
                </Drawer>
            </AIFilterSearchContext.Provider>
        );
    }
);
